// PrimeReact Calendar: onChange(e) => e.value (Date | Date[] | null).
// Verified from existing usages:
//   - apps/web/src/modules/reports/ComplianceReports.tsx:41
//       onChange={e => setDateRange(e.value as [Date, Date])} selectionMode="range"
//   - apps/web/src/modules/reports/HistoricalReports.tsx:97
//       onChange={(e) => setDateFrom((e.value as Date | null) ?? null)}
//   - apps/web/src/modules/reports/HistoricalReports.tsx:111
//       onChange={(e) => setDateTo((e.value as Date | null) ?? null)}
// PrimeReact Calendar uses `inputId` (not `id`) to set the id on the inner <input>.
// Verified at apps/web/node_modules/primereact/calendar/calendar.d.ts:630 — inputId?: string
//
// Note: selectionMode='range' produces Date[] of length 2 — intended for FormDateRange (UI-9b).

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Calendar } from 'primereact/calendar'

/** The value accepted by PrimeReact's Calendar across single/range/multiple selection modes. */
type CalendarValue = Date | Date[] | (Date | null)[] | null | undefined

interface Props {
  name: string
  label: string
  helpText?: string
  required?: boolean
  disabled?: boolean
  showTime?: boolean
  /** Render a time-only picker (no date). Pairs with hourFormat. */
  timeOnly?: boolean
  /** '12' or '24' — controls the time picker presentation. */
  hourFormat?: '12' | '24'
  dateFormat?: string
  selectionMode?: 'single' | 'multiple' | 'range'
  showIcon?: boolean
  placeholder?: string
}

export function FormCalendar({
  name,
  label,
  helpText,
  required,
  disabled,
  showTime,
  timeOnly,
  hourFormat,
  dateFormat,
  selectionMode = 'single',
  showIcon,
  placeholder,
}: Props): JSX.Element {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy =
    [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && <span aria-hidden> *</span>}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Calendar
            inputId={id}
            value={field.value as CalendarValue}
            onChange={(e) => field.onChange(e.value)}
            onBlur={field.onBlur}
            disabled={disabled}
            showTime={showTime}
            timeOnly={timeOnly}
            hourFormat={hourFormat}
            dateFormat={dateFormat}
            selectionMode={selectionMode}
            showIcon={showIcon}
            placeholder={placeholder}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        )}
      />
      {helpText && (
        <small id={helpId} className="text-gray-500">
          {helpText}
        </small>
      )}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600">
          {errorMessage}
        </small>
      )}
    </div>
  )
}
